/**
 * Pure helpers for the Bills screens (AB#6872): the bucket rule shared with the dashboard
 * and the server, the Needs attention "Problem" text, the cross-page selection used by
 * Convert to check, and the Bill detail "What happened" timeline.
 *
 * PURE module: no React, MUI, axios or auth imports, so it is unit-testable.
 */

/** Where a bill stands, counted by the bill's own status: the shared rule (utils/billBucket). */
import { bucketFor as billBucket } from "../../utils/billBucket";
import type { BillBucket } from "../../utils/billBucket";

export { billBucket };
export type { BillBucket };

const lowerTrim = (value?: string | null): string =>
  (value ?? "").trim().toLowerCase();

/**
 * Convert to check is offered for ACH bills only. Same test as before AB#6872, made
 * null-safe: `paymentMethod?.toLowerCase() === "ach"`.
 */
export function isAchMethod(paymentMethod?: string | null): boolean {
  return paymentMethod?.toLowerCase() === "ach";
}

/** Which system reported a bill's problem: Tipalti pays ACH, QuickBooks writes checks. */
export function problemSource(
  paymentMethod?: string | null
): "Tipalti" | "QuickBooks" | null {
  const m = lowerTrim(paymentMethod);
  if (m === "ach") return "Tipalti";
  if (m === "check") return "QuickBooks";
  return null;
}

export const NO_REASON_RECORDED = "No reason was recorded.";
export const NOT_SENT_YET = "Not sent yet";

/** Prefixes a stored error with the system that reported it ("Tipalti: …"), once. */
export function withProblemSource(
  message: string,
  paymentMethod?: string | null
): string {
  const text = message.trim();
  const source = problemSource(paymentMethod);
  if (!source) return text;
  if (text.toLowerCase().startsWith(source.toLowerCase())) return text;
  return `${source}: ${text}`;
}

export interface ProblemInput {
  billStatus?: string | null;
  paymentStatus?: string | null;
  paymentMethod?: string | null;
  errorMessage?: string | null;
}

export interface BillProblem {
  /** 'failed' rows read as an error; 'notSent' rows as a muted note. */
  kind: "failed" | "notSent";
  text: string;
}

/**
 * The Needs attention "Problem" cell. Only failed and not-sent bills have one (null for
 * everything else, e.g. bills still with Tipalti): the stored error with its source, else
 * "Not sent yet" for a bill that was never sent, else "No reason was recorded.".
 */
export function billProblem(row: ProblemInput): BillProblem | null {
  const bucket = billBucket(row.billStatus, row.paymentStatus);
  if (bucket !== "failed" && bucket !== "notSent") return null;
  const message = (row.errorMessage ?? "").trim();
  if (message) {
    return {
      kind: bucket,
      text: withProblemSource(message, row.paymentMethod),
    };
  }
  return {
    kind: bucket,
    text: bucket === "notSent" ? NOT_SENT_YET : NO_REASON_RECORDED,
  };
}

// ---------------------------------------------------------------------------
// Selection (Needs attention -> Convert to check)
// ---------------------------------------------------------------------------

/** The fields a selected row must keep so its name and amount survive paging. */
export interface SelectableBill {
  billRefCode: string;
  payeeName?: string | null;
  billAmount?: number | null;
}

/** Selected rows keyed by billRefCode. */
export type BillSelection<T extends SelectableBill> = Map<string, T>;

const hasRefCode = (row: SelectableBill): boolean =>
  typeof row.billRefCode === "string" && row.billRefCode.trim() !== "";

/** A row can be selected only when it has a bill ref code to send to ConvertToCheck. */
export const isSelectable = hasRefCode;

/**
 * Adds every selectable row of a page. AB#5377: keyed by billRefCode, so a code carried
 * by two rows is selected (and later posted to ConvertToCheck) only once.
 */
export function selectRows<T extends SelectableBill>(
  selection: BillSelection<T>,
  rows: T[]
): BillSelection<T> {
  const next = new Map(selection);
  rows.forEach((row) => {
    if (hasRefCode(row) && !next.has(row.billRefCode)) {
      next.set(row.billRefCode, row);
    }
  });
  return next;
}

/** Removes the given rows (by billRefCode) from the selection. */
export function deselectRows<T extends SelectableBill>(
  selection: BillSelection<T>,
  rows: T[]
): BillSelection<T> {
  const next = new Map(selection);
  rows.forEach((row) => next.delete(row.billRefCode));
  return next;
}

/** Selects or unselects one row. */
export function toggleRow<T extends SelectableBill>(
  selection: BillSelection<T>,
  row: T
): BillSelection<T> {
  if (!hasRefCode(row)) return selection;
  return selection.has(row.billRefCode)
    ? deselectRows(selection, [row])
    : selectRows(selection, [row]);
}

/** Header checkbox state for the rows on the current page. */
export function pageSelectionState<T extends SelectableBill>(
  selection: BillSelection<T>,
  rows: T[]
): { checked: boolean; indeterminate: boolean; selectable: number } {
  const codes = Array.from(
    new Set(rows.filter(hasRefCode).map((row) => row.billRefCode))
  );
  const selectedOnPage = codes.filter((code) => selection.has(code)).length;
  return {
    checked: codes.length > 0 && selectedOnPage === codes.length,
    indeterminate: selectedOnPage > 0 && selectedOnPage < codes.length,
    selectable: codes.length,
  };
}

/** The unique billRefCodes posted to ConvertToCheck (AB#5377). */
export function selectedRefCodes<T extends SelectableBill>(
  selection: BillSelection<T>
): string[] {
  return Array.from(selection.keys());
}

/** Sum of the selected bills' amounts (missing or non-numeric amounts count as 0). */
export function selectionTotal<T extends SelectableBill>(
  selection: BillSelection<T>
): number {
  let total = 0;
  selection.forEach((row) => {
    const amount = Number(row.billAmount);
    if (Number.isFinite(amount)) total += amount;
  });
  return total;
}

// ---------------------------------------------------------------------------
// Wording
// ---------------------------------------------------------------------------

/** "1 bill" / "3 bills" (with thousands separators). */
export function billCount(n: number): string {
  return `${n.toLocaleString("en-US")} ${n === 1 ? "bill" : "bills"}`;
}

/** Title of the Convert to check dialog. */
export function convertTitle(n: number): string {
  if (n <= 0) return "Convert to check?";
  return n === 1
    ? "Convert 1 bill to a check?"
    : `Convert ${billCount(n)} to checks?`;
}

/** Success toast after Convert to check. */
export function convertedMessage(n: number): string {
  return n === 1
    ? "Converted 1 bill to a check"
    : `Converted ${billCount(n)} to checks`;
}

/** Banner title on Needs attention. */
export function attentionTitle(n: number): string {
  if (n === 0) return "Every payment has gone through";
  return n === 1
    ? "1 payment didn't go through"
    : `${n.toLocaleString("en-US")} payments didn't go through`;
}

/**
 * The message to show for a failed request: the API's `message` (ApiResponse), the
 * validation messages the axios interceptor throws as an array, an Error's message, or
 * the fallback. Never includes the response body itself.
 */
export function errorMessageOf(error: unknown, fallback: string): string {
  if (Array.isArray(error)) {
    const parts = error.filter((e) => typeof e === "string" && e.trim());
    if (parts.length) return parts.join(" ");
  }
  if (error && typeof error === "object") {
    const e = error as {
      data?: { message?: unknown };
      response?: { data?: { message?: unknown } };
      message?: unknown;
    };
    const candidates = [e.data?.message, e.response?.data?.message, e.message];
    const found = candidates.find(
      (c): c is string => typeof c === "string" && c.trim() !== ""
    );
    if (found) return found;
  }
  return fallback;
}

// ---------------------------------------------------------------------------
// Bill detail
// ---------------------------------------------------------------------------

export interface PersonName {
  first?: string | null;
  middle?: string | null;
  last?: string | null;
}

/** "First Middle Last", skipping empty parts. */
export function fullName(name?: PersonName | null): string {
  if (!name) return "";
  return [name.first, name.middle, name.last]
    .map((part) => (part ?? "").trim())
    .filter(Boolean)
    .join(" ");
}

/** Who the bill pays, printed once: payTo, else the bill's name, else the payee's name. */
export function paidToName(
  bill: { payTo?: string | null; name?: string | null } | null | undefined,
  payeeName: string
): string {
  const payTo = (bill?.payTo ?? "").trim();
  if (payTo) return payTo;
  const billName = (bill?.name ?? "").trim();
  if (billName) return billName;
  return payeeName.trim();
}

export interface AddressParts {
  address1?: string | null;
  address2?: string | null;
  address3?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
}

/** Address as display lines: street lines, then "City, ST 12345". Empty when none. */
export function addressLines(address?: AddressParts | null): string[] {
  if (!address) return [];
  const clean = (v?: string | null) => (v ?? "").trim();
  const street = [address.address1, address.address2, address.address3]
    .map(clean)
    .filter(Boolean);
  const city = clean(address.city);
  const stateZip = [clean(address.state), clean(address.postalCode)]
    .filter(Boolean)
    .join(" ");
  const last = [city, stateZip].filter(Boolean).join(", ");
  return last ? [...street, last] : street;
}

/** A date worth showing: parsable and not a default (year 1) placeholder. */
export function meaningfulDate(d?: string | null): string | null {
  if (!d || !d.trim()) return null;
  const date = new Date(d);
  if (Number.isNaN(date.getTime()) || date.getUTCFullYear() < 1900) return null;
  return d;
}

export interface TimelineBill {
  status?: string | null;
  paymentMethod?: string | null;
  invoiceId?: string | null;
  createdDate?: string | null;
  lastQBRetryDate?: string | null;
  qbLastErrorCode?: string | null;
  paymentService?: {
    paymentStatus?: string | null;
    paymentDate?: string | null;
  } | null;
}

export interface TimelineEvent {
  key: "received" | "sent" | "quickbooks" | "paid" | "failed";
  title: string;
  /** ISO date when the step is dated; null for undated steps. */
  date: string | null;
  detail?: string;
  tone: "ok" | "info" | "err";
}

/**
 * "What happened" to a bill, built only from data CSI Pay stores: received from COMPASS
 * (createdDate · invoice), sent to Tipalti (ACH that reached Tipalti, undated), the last
 * QuickBooks failure (lastQBRetryDate · error code), paid (paymentDate) and failed (undated).
 */
export function billTimeline(bill: TimelineBill): TimelineEvent[] {
  const events: TimelineEvent[] = [];
  const s = lowerTrim(bill.status);
  const invoiceId = (bill.invoiceId ?? "").trim();

  events.push({
    key: "received",
    title: "Received from COMPASS",
    date: meaningfulDate(bill.createdDate),
    detail: invoiceId ? `Invoice ${invoiceId}` : undefined,
    tone: "ok",
  });

  if (
    isAchMethod(bill.paymentMethod) &&
    (s === "pendingpayment" || s === "paid" || s === "sent")
  ) {
    events.push({
      key: "sent",
      title: "Sent to Tipalti",
      date: null,
      tone: "info",
    });
  }

  const qbDate = meaningfulDate(bill.lastQBRetryDate);
  const qbCode = (bill.qbLastErrorCode ?? "").trim();
  if (qbDate || qbCode) {
    events.push({
      key: "quickbooks",
      title: "QuickBooks didn't create the check",
      date: qbDate,
      detail: qbCode || undefined,
      tone: "err",
    });
  }

  if (billBucket(bill.status, bill.paymentService?.paymentStatus) === "paid") {
    events.push({
      key: "paid",
      title: "Paid",
      date: meaningfulDate(bill.paymentService?.paymentDate),
      tone: "ok",
    });
  }

  if (
    billBucket(bill.status, bill.paymentService?.paymentStatus) === "failed"
  ) {
    events.push({ key: "failed", title: "Failed", date: null, tone: "err" });
  }

  return events;
}
